/**
 * Approval Reviewer Portal — public, magic-link-authed.
 *
 * Design intent: a board member clicks the link in their inbox and arrives
 * directly at the materials and decision form. No splash. No clicks before
 * they can start reading. Everything lives on one scrollable page with a
 * sticky decision bar; on submit, a receipt screen they can print or save.
 *
 * Pillars:
 *   1. Single-page review experience — no tab juggling
 *   2. Sender clearly identified at top (who's asking + why)
 *   3. Auto-track "viewed" via IntersectionObserver — decision unlocks when
 *      all required materials have entered the viewport
 *   4. Sticky decision bar (always visible on mobile, inline anchor on desktop)
 *   5. Confirmation modal before submit (legal weight — typed name = signature)
 *   6. Receipt screen exposes audit details (IP, UA, timestamp, audit ID) so
 *      reviewers can independently verify what was recorded — and print it
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  NoSymbolIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || '';

const DECISIONS = [
  { value: 'approved',                 label: 'Approve',                  tone: 'emerald' },
  { value: 'approved_with_conditions', label: 'Approve with conditions',  tone: 'emerald' },
  { value: 'rejected',                 label: 'Reject',                   tone: 'red' },
  { value: 'abstained',                label: 'Abstain',                  tone: 'slate' },
  { value: 'recused',                  label: 'Recuse',                   tone: 'slate' },
];

const TONE_RING = {
  emerald: 'border-emerald-500 bg-emerald-50 text-emerald-800 ring-emerald-200',
  red:     'border-red-500 bg-red-50 text-red-700 ring-red-200',
  slate:   'border-slate-400 bg-slate-50 text-slate-700 ring-slate-200',
};

function fmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }
function fmtRelative(d) {
  if (!d) return '';
  const ms = new Date(d).getTime() - Date.now();
  const days = Math.round(ms / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  if (days > 0)   return `in ${days} days`;
  if (days === -1) return 'yesterday';
  return `${Math.abs(days)} days ago`;
}

export default function ReviewerPortal({ token }) {
  const [stage, setStage] = useState('loading'); // loading | review | submitting | receipt | error
  const [data, setData] = useState(null);
  const [viewed, setViewed] = useState({});
  const [decision, setDecision] = useState('approved');
  const [comment, setComment] = useState('');
  const [signature, setSignature] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const decisionPanelRef = useRef(null);

  // ---- Bootstrap: preflight + auto-load full package ----------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pf = await fetch(`${API}/api/approval-portal/${token}/preflight`);
        const pre = await pf.json();
        if (cancelled) return;
        if (!pre.success) {
          setError(pre.message || 'This invitation is no longer valid.');
          setStage('error'); return;
        }
        // Already decided? Skip to receipt.
        if (pre.preview?.already_decided) {
          // Still fetch the package for full receipt context.
          const pkgRes = await fetch(`${API}/api/approval-portal/package`, {
            headers: { 'x-approval-token': token },
          });
          const pkg = await pkgRes.json();
          if (cancelled) return;
          if (pkg.success) setData(pkg);
          setStage('receipt');
          return;
        }
        // Otherwise pull the package and land directly in review.
        const pkgRes = await fetch(`${API}/api/approval-portal/package`, {
          headers: { 'x-approval-token': token },
        });
        const pkg = await pkgRes.json();
        if (cancelled) return;
        if (!pkg.success) {
          setError(pkg.message || 'Unable to load the package.');
          setStage('error'); return;
        }
        setData(pkg);
        setStage('review');
      } catch (e) {
        if (!cancelled) { setError('Unable to reach the approval service.'); setStage('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ---- Auto-track "viewed" as each policy card enters the viewport, AND
  //      track which one is currently most-visible (for scroll-spy nav).
  const [activeItemId, setActiveItemId] = useState(null);
  useEffect(() => {
    if (stage !== 'review' || !data) return;
    const observer = new IntersectionObserver(
      (entries) => {
        setViewed((prev) => {
          const next = { ...prev };
          for (const e of entries) {
            if (e.isIntersecting && e.intersectionRatio > 0.25) {
              const id = e.target.getAttribute('data-item-id');
              if (id) next[id] = true;
            }
          }
          return next;
        });
        // Pick the entry closest to the top of the viewport as "active".
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const top = visible.reduce((a, b) =>
            Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b
          );
          const id = top.target.getAttribute('data-item-id');
          if (id) setActiveItemId(id);
        }
      },
      { threshold: [0, 0.25, 0.5, 1] }
    );
    document.querySelectorAll('[data-item-id]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [stage, data]);

  const jumpToItem = (id) => {
    const el = document.querySelector(`[data-item-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const allViewed = data?.items?.length > 0 && data.items.every((i) => viewed[i.item_id]);

  // ---- Submit -------------------------------------------------------------
  const doSubmit = async () => {
    setConfirming(false);
    setStage('submitting');
    setError('');
    try {
      const res = await fetch(`${API}/api/approval-portal/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-approval-token': token },
        body: JSON.stringify({ decision, signature: signature.trim(), comment: comment || null }),
      });
      const out = await res.json();
      if (!out.success) throw new Error(out.message || 'Unable to record your decision.');
      // Refresh receipt details (decision_at, etc.) by re-fetching the package
      const pkgRes = await fetch(`${API}/api/approval-portal/package`, {
        headers: { 'x-approval-token': token },
      });
      const pkg = await pkgRes.json();
      if (pkg.success) setData(pkg);
      setStage('receipt');
    } catch (e) {
      setError(e.message);
      setStage('review');
    }
  };

  const jumpToDecision = useCallback(() => {
    decisionPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ===== RENDER =============================================================
  if (stage === 'loading') {
    return (
      <Shell>
        <div className="text-center py-24 text-slate-400 text-sm">Validating your secure invitation…</div>
      </Shell>
    );
  }

  if (stage === 'error') {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-red-200 p-8 text-center shadow-sm max-w-lg mx-auto">
          <NoSymbolIcon className="w-12 h-12 mx-auto mb-3 text-red-400" strokeWidth={1.5} />
          <h2 className="text-lg font-bold text-slate-800">We can't open this invitation</h2>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
          <p className="text-xs text-slate-400 mt-4">
            If you received multiple emails, please use the most recent one — each reminder issues a fresh secure link.
          </p>
        </div>
      </Shell>
    );
  }

  if (stage === 'receipt' && data) {
    return <Receipt data={data} />;
  }

  // ----- REVIEW + DECIDE (single scrollable page) --------------------------
  return (
    <Shell>
      <Header data={data} allViewed={allViewed} onJumpToDecision={jumpToDecision} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-40">
        {/* Custom message from the compliance office */}
        {data.package?.message && (
          <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4 mb-6">
            <div className="text-xs uppercase font-bold tracking-wider text-indigo-700 mb-1">A note from {data.sender?.name || 'the compliance office'}</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{data.package.message}</p>
          </div>
        )}

        {/* Jump nav — only when there are multiple items in the bundle.
            Horizontally scrollable on mobile; sticky just under the page
            header so a board member can hop between items at any scroll. */}
        {data.items.length > 1 && (
          <nav
            className="sticky top-[112px] sm:top-[96px] z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 mb-4
                       bg-gradient-to-b from-white/95 to-white/70 backdrop-blur border-y border-slate-200/60 print:hidden"
            aria-label="Jump to item"
          >
            <div className="flex gap-2 overflow-x-auto pb-1">
              {data.items.map((item, idx) => {
                const isActive = activeItemId === item.item_id;
                const isViewed = !!viewed[item.item_id];
                return (
                  <button
                    key={item.item_id}
                    onClick={() => jumpToItem(item.item_id)}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                        : isViewed
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-300'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    title={item.artifact_title || item.policy_title}
                  >
                    <span className={`w-4 h-4 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white text-indigo-700' :
                      isViewed ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {isViewed && !isActive ? '✓' : idx + 1}
                    </span>
                    <span className="max-w-[180px] truncate">{item.artifact_title || item.policy_title}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        )}

        {/* Materials */}
        <div className="space-y-4">
          {data.items.map((item, idx) => (
            <ArtifactCard key={item.item_id} item={item} idx={idx} total={data.items.length} viewed={viewed} />
          ))}
        </div>

        {/* Decision panel */}
        <section
          ref={decisionPanelRef}
          className="mt-10 bg-white border border-slate-200 rounded-2xl shadow-sm p-6"
        >
          <div className="text-xs uppercase font-bold tracking-wider text-indigo-600">Your decision</div>
          <h2 className="text-xl font-bold text-slate-900 mt-1">Record your decision</h2>
          <p className="text-sm text-slate-500 mt-1">
            Your decision applies to all materials in this package and is final once submitted.
          </p>

          {!allViewed && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              Scroll through each policy above before submitting. {Object.keys(viewed).length} of {data.items.length} reviewed so far.
            </div>
          )}

          <div className="mt-5">
            <div className="grid sm:grid-cols-2 gap-2">
              {DECISIONS.map((d) => (
                <label
                  key={d.value}
                  className={`flex items-center gap-2 px-3 py-3 rounded-xl border cursor-pointer text-sm transition-all ${
                    decision === d.value
                      ? `${TONE_RING[d.tone]} ring-4 ring-opacity-40`
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <input
                    type="radio"
                    checked={decision === d.value}
                    onChange={() => setDecision(d.value)}
                    className="text-indigo-600"
                  />
                  <span className="font-medium">{d.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
              Comments {decision === 'rejected' || decision === 'approved_with_conditions' ? <span className="text-red-500">(strongly recommended)</span> : '(optional)'}
            </label>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                decision === 'rejected' ? 'Why are you rejecting?'
                : decision === 'approved_with_conditions' ? 'What conditions must be met?'
                : 'Notes for the record…'
              }
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            />
          </div>

          <div className="mt-5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
              Type your full legal name to sign *
            </label>
            <input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Your full legal name"
              className="w-full text-base border border-slate-200 rounded-lg px-3 py-2 font-serif italic focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoComplete="name"
            />
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              By typing your name above and submitting, you are providing an electronic signature with the same
              legal effect as a handwritten signature. Your name, the time of signing, your IP address, and your
              decision will be permanently recorded in the audit trail for this package.
            </p>
          </div>

          {error && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
              {error}
            </div>
          )}
        </section>
      </main>

      {/* Sticky decision bar — always visible at the bottom on every device. */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-[0_-4px_20px_-12px_rgba(0,0,0,0.15)] z-30 print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 hidden sm:block">
            {allViewed
              ? <>All materials reviewed · ready to submit</>
              : <>{Object.keys(viewed).length}/{data.items.length} reviewed</>}
          </div>
          <button
            onClick={() => setConfirming(true)}
            disabled={!allViewed || !signature.trim() || stage === 'submitting'}
            className={`w-full sm:w-auto px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
              !allViewed || !signature.trim() || stage === 'submitting'
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
            }`}
          >
            {stage === 'submitting' ? 'Recording your decision…' : 'Submit my decision'}
          </button>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirming && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 print:hidden">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="font-bold text-slate-900 text-lg">Confirm your decision</h3>
            <p className="text-sm text-slate-600 mt-2">
              You are about to submit <strong>{DECISIONS.find((d) => d.value === decision)?.label}</strong> for{' '}
              <strong>{data.package.name}</strong>. This is a legally-binding electronic signature and cannot be undone.
            </p>
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
              <div className="text-slate-500">Signing as</div>
              <div className="text-slate-900 font-serif italic text-base">{signature}</div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Go back
              </button>
              <button
                onClick={doSubmit}
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              >
                Sign &amp; submit
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ---------- Per-artifact renderer dispatcher -------------------------------
// Every artifact type lives in its own renderer. Adding a new type is a
// matter of writing one component + adding a case below.
function ArtifactCard({ item, idx, total, viewed }) {
  const isViewed = !!viewed[item.item_id];
  const header = (
    <header className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] uppercase font-bold tracking-wider text-slate-400">
          Item {idx + 1} of {total}
        </div>
        <h3 className="text-lg font-bold text-slate-900 mt-0.5">
          {item.artifact_title || item.policy_title || 'Untitled'}
        </h3>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {item.artifact_subtitle || item.policy_number || ''}
          {(item.artifact_version_label || item.version_number) &&
            <> · {item.artifact_version_label || `v${item.version_number}`}</>}
          {item.effective_from && ` · effective ${new Date(item.effective_from).toLocaleDateString()}`}
        </div>
      </div>
      <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full font-semibold ${
        isViewed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
      }`}>
        {isViewed ? '✓ Reviewed' : 'Not yet reviewed'}
      </span>
    </header>
  );

  let body;
  if (item.artifact_type === 'coa_work_plan') {
    body = <WorkPlanBody payload={item.snapshot || {}} />;
  } else if (item.artifact_type === 'privacy_annual_report') {
    body = <PrivacyReportBody payload={item.snapshot || {}} />;
  } else {
    // Default = ppm_policy_version + any legacy/future text-heavy artifact.
    body = <PolicyBody item={item} />;
  }

  return (
    <article
      data-item-id={item.item_id}
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
    >
      {header}
      {body}
    </article>
  );
}

// The Privacy program's report to the board: eight sections, the facts from
// the record in plain sentences, the Privacy Officer's statement under each.
function PrivacyReportBody({ payload }) {
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }) : '');
  return (
    <div className="px-6 py-5 space-y-5">
      <p className="text-[12.5px] text-slate-500">Period {day(payload.period_start)} to {day(payload.period_end)}{payload.sealed_at ? ` · sealed ${day(payload.sealed_at)}` : ''}{payload.sha256 ? ` · record ${String(payload.sha256).slice(0, 16)}…` : ''}</p>
      {sections.map((s) => (
        <section key={s.key}>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1 mb-2">{s.n}. {s.title}</h4>
          {(s.facts || []).map((f, i) => <p key={i} className="text-[13.5px] leading-relaxed text-slate-800 mb-1">{f}</p>)}
          {s.narrative
            ? <div className="mt-2 pl-3 border-l-2 border-slate-300 bg-slate-50 rounded-r-lg py-2 pr-3"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Privacy Officer</div><p className="text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap">{s.narrative}</p></div>
            : <p className="text-xs text-slate-400 mt-1">No statement from the Privacy Officer on this section.</p>}
        </section>
      ))}
    </div>
  );
}

function PolicyBody({ item }) {
  return (
    <>
      {item.change_summary && (
        <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <strong>Change summary:</strong> {item.change_summary}
        </div>
      )}
      <div className="px-6 py-5">
        <pre className="whitespace-pre-wrap font-sans text-[14px] leading-[1.65] text-slate-800">
          {item.content}
        </pre>
      </div>
    </>
  );
}

function WorkPlanBody({ payload }) {
  const summary = payload.summary || { total: 0, by_component: {}, by_type: {}, by_frequency: {} };
  const activities = payload.activities || [];
  return (
    <div className="px-6 py-5">
      {/* At-a-glance summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Tile label="Total activities">
          <div className="text-2xl font-bold text-slate-900">{summary.total}</div>
        </Tile>
        <Tile label="Plan year">
          <div className="text-2xl font-bold text-slate-900">{payload.plan_year || '—'}</div>
        </Tile>
        <Tile label="Components">
          <div className="text-sm text-slate-700">{Object.keys(summary.by_component || {}).join(', ') || '—'}</div>
        </Tile>
        <Tile label="Frequencies">
          <div className="text-sm text-slate-700">{Object.keys(summary.by_frequency || {}).join(', ') || '—'}</div>
        </Tile>
      </div>

      {/* Activities */}
      <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-3">Activities in this plan</div>
      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
        {activities.map((a, i) => (
          <li key={i} className="px-4 py-3 hover:bg-slate-50">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">{a.entity_name || a.activity_type || `Activity ${i + 1}`}</span>
                  {a.component && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-bold uppercase tracking-wider">
                      {a.component}
                    </span>
                  )}
                  {a.activity_type && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                      {a.activity_type}
                    </span>
                  )}
                  {a.activity_frequency && (
                    <span className="text-[10px] text-slate-500">{a.activity_frequency}</span>
                  )}
                </div>
                {(a.activity_description || a.activity_rationale) && (
                  <div className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap">
                    {a.activity_description || a.activity_rationale}
                  </div>
                )}
                {(a.activity_start_date || a.activity_completion_date) && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    {a.activity_start_date && `Starts ${new Date(a.activity_start_date).toLocaleDateString()}`}
                    {a.activity_start_date && a.activity_completion_date && ' · '}
                    {a.activity_completion_date && `Due ${new Date(a.activity_completion_date).toLocaleDateString()}`}
                  </div>
                )}
                {a.departments?.length > 0 && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Departments: {a.departments.join(', ')}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
        {activities.length === 0 && (
          <li className="px-4 py-6 text-center text-xs text-slate-400">No activities in this plan.</li>
        )}
      </ul>

      {payload.approved_by && (
        <p className="text-[11px] text-slate-500 mt-4">
          Previously approved internally by <span className="font-semibold text-slate-700">{payload.approved_by.name}</span>{payload.approved_by.title ? `, ${payload.approved_by.title}` : ''}
          {payload.approved_at && ` on ${new Date(payload.approved_at).toLocaleDateString()}`}.
        </p>
      )}
    </div>
  );
}

// ---------- Shell ----------------------------------------------------------
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50 print:bg-white">
      {children}
      <footer className="text-center text-[11px] text-slate-400 py-6 print:hidden">
        Precision Compliance Group · SOC 2 audit-grade approval workflow · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

// ---------- Top banner (sender + package + deadline) -----------------------
function Header({ data, allViewed, onJumpToDecision }) {
  const orgName = data.organization?.name || 'Your organization';
  const recipient = data.recipient || {};
  const sender = data.sender || {};
  const deadlineLabel = data.package?.deadline_at ? fmtRelative(data.package.deadline_at) : '';

  return (
    <header className="bg-white/80 backdrop-blur border-b border-slate-200 sticky top-0 z-20 print:hidden">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">PCG</div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">SignoffHub · Secure portal</div>
            <div className="text-sm font-bold text-slate-900 truncate">{orgName}</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 hidden md:flex items-center gap-1.5"><LockClosedIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> TLS · audit-logged · single-use link</div>
      </div>

      {/* Title strip */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-5">
        <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-bold">Ratification requested</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 leading-tight">{data.package.name}</h1>
        {data.package.description && (
          <p className="text-sm text-slate-600 mt-2">{data.package.description}</p>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <Tile label="Sent by">
            <div className="text-slate-900 font-semibold">{sender.name || `${orgName} Compliance`}</div>
            {sender.title && <div className="text-slate-500">{sender.title}</div>}
          </Tile>
          <Tile label="Reviewing as">
            <div className="text-slate-900 font-semibold">{recipient.display_name || recipient.email}</div>
            <div className="text-slate-500 capitalize">{recipient.role}{recipient.required === false ? ' (optional)' : ''}</div>
          </Tile>
          <Tile label="Deadline">
            <div className="text-slate-900 font-semibold">{fmtDate(data.package.deadline_at)}</div>
            {deadlineLabel && <div className="text-slate-500">{deadlineLabel}</div>}
          </Tile>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={onJumpToDecision}
            className={`text-xs px-3 py-2 rounded-lg font-semibold transition-all ${
              allViewed
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {allViewed ? 'Jump to decision →' : `Read first · ${data.items.length} ${data.items.length === 1 ? 'policy' : 'policies'}`}
          </button>
        </div>
      </div>
    </header>
  );
}

function Tile({ label, children }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// ---------- Receipt (post-submission) --------------------------------------
function Receipt({ data }) {
  const r = data.recipient || {};
  const pkg = data.package || {};
  const decisionLabel = (r.decision || '').replace(/_/g, ' ');
  const auditId = (r.id || '').slice(-12);
  const isApproval = ['approved', 'approved_with_conditions'].includes(r.decision);

  return (
    <Shell>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm print:shadow-none print:border-0">
          <div>
            {isApproval
              ? <CheckCircleIcon className="w-12 h-12 text-emerald-500" strokeWidth={1.5} />
              : r.decision === 'rejected'
                ? <XCircleIcon className="w-12 h-12 text-red-500" strokeWidth={1.5} />
                : <PencilSquareIcon className="w-12 h-12 text-slate-500" strokeWidth={1.5} />}
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mt-2">Your decision is recorded.</h1>
          <p className="text-sm text-slate-600 mt-2">
            Thank you. The compliance office has been notified. You can print or save this page for your records.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
            <Tile label="Package">
              <div className="text-slate-900 font-semibold">{pkg.name}</div>
            </Tile>
            <Tile label="Decision">
              <div className="text-slate-900 font-semibold capitalize">{decisionLabel}</div>
            </Tile>
            <Tile label="Reviewer">
              <div className="text-slate-900 font-semibold">{r.display_name || r.email}</div>
              <div className="text-slate-500">{r.email}</div>
            </Tile>
            <Tile label="Signed at">
              <div className="text-slate-900 font-semibold">{fmtDate(r.decision_at)}</div>
            </Tile>
          </div>

          {r.decision_comment && (
            <div className="mt-5 bg-slate-50 border border-slate-100 rounded-lg p-4">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">Your comment</div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap italic">"{r.decision_comment}"</p>
            </div>
          )}

          <div className="mt-6 bg-slate-50 border border-slate-100 rounded-lg p-4">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">Audit record (SOC 2)</div>
            <dl className="grid grid-cols-3 gap-2 text-xs">
              <dt className="text-slate-500">Audit ID</dt>
              <dd className="col-span-2 font-mono text-slate-900">{auditId}</dd>
              <dt className="text-slate-500">Signature</dt>
              <dd className="col-span-2 font-serif italic text-slate-900">{r.decision_signature}</dd>
              {r.decision_ip && (<>
                <dt className="text-slate-500">From IP</dt>
                <dd className="col-span-2 font-mono text-slate-900">{r.decision_ip}</dd>
              </>)}
            </dl>
            <p className="text-[10px] text-slate-400 mt-3">
              This record is immutable in the compliance audit log and cannot be edited or deleted by anyone, including platform administrators.
            </p>
          </div>

          <div className="mt-6 flex gap-2 print:hidden">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            >
              Print or save as PDF
            </button>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Close window
            </button>
          </div>
        </div>
      </main>
    </Shell>
  );
}
